import DeleteIcon from '@mui/icons-material/Delete';
import type { Theme } from '@mui/material/styles';

import { Button, Stack } from '@src/components';
import { ALERT_ACTIONS } from '@src/context/constants';
import { useAlert } from '@src/context/hooks';
import { Form, Submit } from '@src/corelib/forms/generic';
import { createSchema, Yup } from '@src/corelib/validation';
import { formatDateRange } from '@src/utils';

import useAbsencesTimeline from './../../../hooks';
import EventFormFieldset from './Fields';

type EventFormValues = {
  dateFrom: Date | null;
  dateTo?: Date | null;
  startTime?: string | null;
  endTime?: string | null;
  acti_type: number | null;
  acti_tentative?: boolean;
  acab_notes?: string;
};

type EventFormProps = {
  hoursView: boolean;
};

type RedPaletteTheme = Theme & {
  palette: Theme['palette'] & { red: Record<number, string> };
};

const schema = createSchema({
  dateFrom: Yup.date().required('This field is required'),
  dateTo: Yup.date().nullable(),
  typeId: Yup.number().required('This field is required'),
  tentative: Yup.boolean(),
});

export default function EventForm({ hoursView }: EventFormProps) {
  const { selectedSlot, setSelectedSlot, book, selectedSlotData, remove } =
    useAbsencesTimeline();
  const alert = useAlert();

  const getEndDate = (values: EventFormValues) => {
    const endDate =
      hoursView || !values.dateTo ? values.dateFrom : values.dateTo;
    return endDate;
  };
  const onSubmit = async (values: EventFormValues) => {
    const payload = {
      ...values,
      userId: selectedSlot?.userId,
      dateFrom: values.dateFrom,
      dateTo: getEndDate(values),
    };

    if (!selectedSlot?.selectedIds.length) {
      return book(payload);
    }

    const { start, end } = selectedSlot;
    const { action } = await alert.open({
      title: 'Warning',
      children: `You are about to overide existing absence events from:
             ${formatDateRange(start, end)}`,
      slotProps: {
        confirmButton: { label: 'OK' },
        cancelButton: { label: 'Cancel' },
      },
    });
    if (action === ALERT_ACTIONS.CONFIRM) {
      return book(payload);
    }
  };
  const deleteAbsences = async () => {
    if (selectedSlot) {
      if (selectedSlot.selectedIds.length) {
        const { start, end } = selectedSlot;
        const { action } = await alert.open({
          title: 'Warning',
          children: `Are you sure you want to remove absence events from:
             ${formatDateRange(start, end)}`,
          slotProps: {
            confirmButton: { label: 'Delete' },
            cancelButton: { label: 'Cancel' },
          },
        });
        if (action === ALERT_ACTIONS.CONFIRM) {
          remove(selectedSlot.selectedIds);
        }
      } else {
        setSelectedSlot(null);
      }
    }
  };

  const getDefaultValues = (): Record<string, unknown> => {
    let dateFrom: Date | null = null;
    let dateTo: Date | null = null;
    let startTime: string | null = null;
    let endTime: string | null = null;
    let tentative: boolean = false;
    if (selectedSlotData) {
      if (selectedSlotData.start) {
        dateFrom = new Date(selectedSlotData.start);
      }
      if (selectedSlotData.end) {
        dateTo = new Date(selectedSlotData.end);
      }
      if (selectedSlotData.startTime) {
        startTime = selectedSlotData.startTime;
      }
      if (selectedSlotData.endTime) {
        endTime = selectedSlotData.endTime;
      }
      tentative = selectedSlotData.tentative
    }
    return {
      dateFrom,
      dateTo,
      startTime,
      endTime,
      tentative
    };
  };
  return (
    <Form
      schema={schema}
      defaultValues={getDefaultValues()}
      onSubmit={(values) => onSubmit(values as EventFormValues)}
    >
      <EventFormFieldset hoursView={hoursView} />
      <Stack direction="row" justifyContent="space-between">
        <Submit variant="contained" color="primary" loadingPosition="start">
          Book
        </Submit>
        <Button
          variant="outlined"
          sx={Stylesheet.delete}
          onClick={deleteAbsences}
        >
          <DeleteIcon />
        </Button>
        <Button disabled variant="contained">
          Approve
        </Button>
      </Stack>
    </Form>
  );
}

const Stylesheet = {
  delete: (theme: Theme) => ({
    minWidth: '30px',
    width: '30px',
    padding: '10px 20px',
    '&:hover': {
      color:
        (theme as RedPaletteTheme).palette.red?.[500] ??
        theme.palette.error.main,
    },
  }),
};
